import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../api';
import { staticImg, stripHtml } from '../utils/media';
import { useToast } from '../hooks/use-toast';
import {
  Download, ShieldCheck, User, Mail, Key, Sparkles, X, CheckCircle2,
  RefreshCw, ShoppingBag, ExternalLink, Lock
} from 'lucide-react';

export default function TestServers() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedServer, setSelectedServer] = useState(null);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [regSuccessData, setRegSuccessData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password_confirm: '',
    delete_code: '1234567',
  });

  const { toast } = useToast();

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    setLoading(true);
    try {
      const res = await API.get('/test-servers');
      if (res.data && res.data.length > 0) {
        setServers(res.data);
      } else {
        setServers([
          {
            id: '1',
            name: '1-99 Hard Emek Test Sunucusu',
            server_type: '1-99 Hard Emek',
            status: 'active',
            description: 'Simyasız, kuşaksız, derecesiz %100 Orijinal 2008 ruhu. Performans ve güvenlik odaklı C++ sunucu altyapısı.',
            client_download_url: 'http://aegistudios.com/Aegis.exe',
            buy_url: '/products?category=server-files',
            image: 'cat-pvp.jpg',
            sort_order: 1
          },
          {
            id: '2',
            name: '1-99 Orta Emek Test Sunucusu',
            server_type: '1-99 Orta Emek',
            status: 'active',
            description: 'Dengeli ekonomi, gelişmiş zindan takip sistemi, biyolog & oto-av desteğiyle akıcı test yapısı.',
            client_download_url: 'http://aegistudios.com/Aegis.exe',
            buy_url: '/products?category=systems',
            image: 'cat-systems.jpg',
            sort_order: 2
          },
          {
            id: '3',
            name: '1-120 Official Test Sunucusu',
            server_type: '1-120 Official',
            status: 'active',
            description: 'Yohara kıtası, Elementer Dünya, Şampiyon Seviyeleri ve 2026 güncel zindan altyapısı hazır.',
            client_download_url: 'http://aegistudios.com/Aegis.exe',
            buy_url: '/products?category=models',
            image: 'cat-models.jpg',
            sort_order: 3
          }
        ]);
      }
    } catch (err) {
      console.error('Error loading test servers:', err);
    } finally {
      setLoading(false);
    }
  };

  const openRegisterModal = (server) => {
    setSelectedServer(server);
    setFormData({
      username: '',
      email: '',
      password: '',
      password_confirm: '',
      delete_code: '1234567',
    });
    setRegSuccessData(null);
    setRegisterModalOpen(true);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.email || !formData.password) {
      toast({ title: 'Eksik Bilgi', description: 'Lütfen tüm zorunlu alanları doldurun.', variant: 'destructive' });
      return;
    }
    if (formData.password !== formData.password_confirm) {
      toast({ title: 'Şifre Uyuşmazlığı', description: 'Girdiğiniz şifreler birbiriyle eşleşmiyor.', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await API.post(`/test-servers/${selectedServer.id}/register`, formData);
      if (res.data && res.data.ok) {
        setRegSuccessData(res.data);
        toast({ title: 'Kayıt Başarılı!', description: 'Hesabınız doğrudan oyun veritabanında oluşturuldu.' });
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Kayıt işlemi sırasında bir hata oluştu.';
      toast({ title: 'Kayıt Başarısız', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080b] text-white flex flex-col font-sans selection:bg-[#e4b44e] selection:text-black">
      <Helmet>
        <title>Test Sunucuları | Aegis Studios</title>
        <meta name="description" content="Aegis Studios Metin2 test sunucularına doğrudan kayıt olun ve oyuna anında bağlanın." />
      </Helmet>

      <Header />

      <main className="flex-1 pt-28 pb-20 px-4 md:px-6 lg:px-10 max-w-[1400px] mx-auto w-full">
        {/* COMPACT PAGE HEADER & TOP RIGHT DOWNLOAD BUTTON */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border-b border-white/10 pb-8 mb-12">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#e4b44e]/10 border border-[#e4b44e]/30 text-[#f4d896] text-xs font-bold tracking-widest uppercase">
              <Sparkles className="w-3.5 h-3.5" /> CANLI TEST SUNUCULARI
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black text-white">
              Test <span className="gold-text-animated">Sunucularımız</span>
            </h1>
            <p className="text-xs md:text-sm text-white/65 max-w-xl">
              Tüm altyapılarımızı ve güncellemelerimizi canlı test ortamında deneyimleyin. Açılır pop-up pencereden kaydınızı açıp istemciyi indirerek anında oyuna girebilirsiniz.
            </p>
          </div>

          {/* TOP RIGHT DOWNLOAD CLIENT BUTTON */}
          <div className="shrink-0">
            <a
              href="http://aegistudios.com/Aegis.exe"
              target="_blank"
              rel="noreferrer"
              className="btn-gold px-6 py-3.5 rounded-2xl text-xs font-extrabold tracking-wider uppercase inline-flex items-center gap-2.5 shadow-lg shadow-[#e4b44e]/20 hover:scale-[1.02] active:scale-[0.98] transition"
            >
              <Download className="w-4 h-4" /> İSTEMCİYİ İNDİR (.EXE)
            </a>
          </div>
        </div>

        {/* TEST SERVERS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {servers.map((srv) => (
            <div
              key={srv.id}
              className="group relative rounded-2xl bg-[#0f0f17] border border-white/10 hover:border-[#e4b44e]/50 p-6 flex flex-col justify-between transition-all duration-300 hover:shadow-xl hover:shadow-black/60"
            >
              <div>
                {/* Card Cover Image */}
                <div className="relative h-48 rounded-xl overflow-hidden mb-5 bg-[#181824] border border-white/5">
                  <img
                    src={staticImg(srv.image || 'cat-pvp.jpg')}
                    alt={srv.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.target.src = '/assets/img/cat-pvp.jpg'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f17] via-transparent to-black/30" />
                  <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#08080c]/80 backdrop-blur border border-[#e4b44e]/30 text-[#f4d896] text-[11px] font-extrabold tracking-wider uppercase">
                    {srv.server_type || 'TEST SUNUCUSU'}
                  </div>
                </div>

                <h3 className="font-display text-xl font-bold text-white group-hover:text-[#f4d896] transition-colors mb-2">
                  {srv.name}
                </h3>
                <p className="text-xs text-white/60 leading-relaxed mb-6">
                  {stripHtml(srv.description)}
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-2.5 pt-4 border-t border-white/10">
                <button
                  onClick={() => openRegisterModal(srv)}
                  className="w-full btn-gold py-3 rounded-xl text-xs font-extrabold tracking-wider uppercase flex items-center justify-center gap-2 hover:scale-[1.01] active:scale-[0.99] transition shadow-md shadow-[#e4b44e]/15"
                >
                  <User className="w-4 h-4" /> HIZLI KAYIT OL (POP-UP)
                </button>

                {/* PURCHASE / PRODUCT PAGE LINK */}
                {srv.buy_url && srv.buy_url.startsWith('http') ? (
                  <a
                    href={srv.buy_url}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-[#e4b44e]/40 hover:bg-[#e4b44e]/10 text-xs font-bold text-white/90 hover:text-[#f4d896] flex items-center justify-center gap-2 transition"
                  >
                    <ShoppingBag className="w-3.5 h-3.5 text-[#f4d896]" /> PAKETİ İNCELE & SATIN AL <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ) : (
                  <Link
                    to={srv.buy_url || '/products?category=server-files'}
                    className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-[#e4b44e]/40 hover:bg-[#e4b44e]/10 text-xs font-bold text-white/90 hover:text-[#f4d896] flex items-center justify-center gap-2 transition"
                  >
                    <ShoppingBag className="w-3.5 h-3.5 text-[#f4d896]" /> PAKETİ İNCELE & SATIN AL
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* POPUP REGISTRATION MODAL */}
      {registerModalOpen && selectedServer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="relative w-full max-w-md rounded-2xl bg-[#0d0d14] border border-[#e4b44e]/40 p-6 md:p-8 shadow-2xl shadow-black">
            <button
              onClick={() => setRegisterModalOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center transition"
            >
              <X className="w-4 h-4" />
            </button>

            {regSuccessData ? (
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-xl font-bold text-white">Kayıt Başarılı!</h3>
                <p className="text-xs text-white/70 leading-relaxed">
                  {regSuccessData.message}
                </p>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-left text-xs space-y-2">
                  <div className="flex justify-between"><span className="text-white/50">Sunucu:</span><span className="font-bold text-[#f4d896]">{selectedServer.name}</span></div>
                  <div className="flex justify-between"><span className="text-white/50">Kullanıcı Adı:</span><span className="font-mono font-bold text-white">{formData.username}</span></div>
                  <div className="flex justify-between"><span className="text-white/50">E-Posta:</span><span className="font-mono text-white/80">{formData.email}</span></div>
                </div>
                <button
                  onClick={() => setRegisterModalOpen(false)}
                  className="w-full btn-gold py-3 rounded-xl text-xs font-bold uppercase tracking-wider"
                >
                  TAMAM / KAPAT
                </button>
              </div>
            ) : (
              <div>
                <div className="mb-6 border-b border-white/10 pb-4">
                  <div className="text-[10px] font-bold tracking-widest text-[#f4d896] uppercase mb-1">
                    OYUN HESABI OLUŞTUR
                  </div>
                  <h3 className="font-display text-lg font-bold text-white">
                    {selectedServer.name}
                  </h3>
                </div>

                <form onSubmit={handleRegisterSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-white/70 mb-1">Kullanıcı Adı *</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-3 w-4 h-4 text-white/40" />
                      <input
                        type="text"
                        required
                        placeholder="Kullanıcı adınız (min 4 karakter)"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-[#e4b44e] text-xs text-white focus:outline-none transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-white/70 mb-1">E-Posta Adresi *</label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-3 w-4 h-4 text-white/40" />
                      <input
                        type="email"
                        required
                        placeholder="E-posta adresiniz"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-[#e4b44e] text-xs text-white focus:outline-none transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-white/70 mb-1">Şifre *</label>
                      <div className="relative">
                        <Lock className="absolute left-3.5 top-3 w-4 h-4 text-white/40" />
                        <input
                          type="password"
                          required
                          placeholder="******"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-[#e4b44e] text-xs text-white focus:outline-none transition"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-white/70 mb-1">Şifre Tekrarı *</label>
                      <div className="relative">
                        <Lock className="absolute left-3.5 top-3 w-4 h-4 text-white/40" />
                        <input
                          type="password"
                          required
                          placeholder="******"
                          value={formData.password_confirm}
                          onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-[#e4b44e] text-xs text-white focus:outline-none transition"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-white/70 mb-1">Karakter Silme Şifresi (7 Haneli PIN)</label>
                    <div className="relative">
                      <Key className="absolute left-3.5 top-3 w-4 h-4 text-white/40" />
                      <input
                        type="text"
                        maxLength={7}
                        placeholder="1234567"
                        value={formData.delete_code}
                        onChange={(e) => setFormData({ ...formData, delete_code: e.target.value })}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 focus:border-[#e4b44e] text-xs text-white focus:outline-none transition"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-2 btn-gold py-3 rounded-xl text-xs font-extrabold uppercase tracking-wider flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4" /> OYUN HESABINI OLUŞTUR
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
